import { VolumeDown, VolumeOff, VolumeUp } from '@mui/icons-material';
import { Box, IconButton, Slider } from '@mui/material';
import { useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { AnalyticsService } from '@/services';
import {
  selectActiveChapterId,
  selectActiveEpisodeId,
  selectPersistedPlayerVolumePreference,
  userPreferencesActions,
} from '@/store';
import { Referrer } from '@/types';

export const VolumeSlider = ({
  iconSize = 24,
  referrer,
}: {
  iconSize?: number;
  referrer: Referrer;
}) => {
  const dispatch = useDispatch();

  const activeEpisodeId = useSelector(selectActiveEpisodeId)!;
  const activeChapterId = useSelector(selectActiveChapterId)!;
  const location = referrer;

  const [sliderValue, setSliderValue] = useState<number>();
  const previousSliderValueRef = useRef<number>(0);
  const playerVolume = useSelector(selectPersistedPlayerVolumePreference);

  const displayVolume = sliderValue || playerVolume;
  const Icon =
    playerVolume === 0 ? VolumeOff : displayVolume < 50 ? VolumeDown : VolumeUp;

  const changeVolume = (val: number) => {
    setSliderValue(val);

    if (
      val !== playerVolume &&
      (Math.abs(val - playerVolume) > 10 || val === 0)
    ) {
      dispatch(userPreferencesActions.setPlaybackVolumePreference(val));
    }
  };

  const onSlide = (val: number) => {
    changeVolume(val);
  };

  const onSlideComplete = (val: number) => {
    if (val > 0) {
      previousSliderValueRef.current = sliderValue!;
    }
    AnalyticsService.trackPlayerVolume({
      chapterId: activeChapterId,
      episodeId: activeEpisodeId,
      val,
      location,
    });
  };

  const handleIconClick = () => {
    if (!previousSliderValueRef.current) {
      previousSliderValueRef.current = displayVolume;
    }

    const targetVolume =
      playerVolume > 0
        ? 0
        : previousSliderValueRef.current > 20
          ? previousSliderValueRef.current
          : 20;

    changeVolume(targetVolume);
    AnalyticsService.trackPlayerVolume({
      chapterId: activeChapterId,
      episodeId: activeEpisodeId,
      val: targetVolume,
      location,
    });
  };

  return (
    <Box sx={styles.container}>
      <IconButton
        sx={styles.volumeIconContainer}
        onClick={handleIconClick}
        aria-label={playerVolume > 0 ? 'Mute' : 'Unmute'}>
        <Icon sx={{ fontSize: iconSize }} />
      </IconButton>
      <Box sx={styles.volumeControlContainer}>
        <Slider
          orientation="horizontal"
          value={displayVolume}
          min={0}
          max={100}
          aria-label="Volume"
          aria-valuetext={`${displayVolume}%`}
          onMouseDown={() =>
            (previousSliderValueRef.current = sliderValue || playerVolume)
          }
          onChange={(_e, val) => onSlide(val as number)}
          onChangeCommitted={(_e, val) => onSlideComplete(val as number)}
          sx={{
            ...styles.root,
            '& .MuiSlider-thumb': styles.thumb,
            '& .MuiSlider-track': styles.track,
            '& .MuiSlider-rail': styles.rail,
          }}
        />
      </Box>
    </Box>
  );
};

const styles = {
  container: {
    alignItems: 'center',
    display: 'flex',
    justifyContent: 'flex-end',
  },
  volumeIconContainer: {
    color: 'secondary.active',
    mr: 2,
    mt: 0.5,
    cursor: 'pointer',
  },
  volumeControlContainer: {
    width: 150,
  },
  root: {
    height: 5,
    py: 1,
    verticalAlign: 'middle',
  },
  track: {
    color: 'secondary.active',
    height: 4,
  },
  rail: {
    color: 'white',
    height: 4,
  },
  thumb: {
    color: 'secondary.active',
    height: 12,
    width: 12,
  },
};
